import React, { useState } from 'react'
import { Link } from 'react-router-dom'

interface Article {
  slug: string
  title: string
  excerpt: string
  category: string
  readTime: string
  publishDate: string
  image: string
}

const articles: Article[] = [
  {
    slug: 'invoice-to-me-guide',
    title: 'Invoice To Me: How to Bill Yourself or Clients as a Sole Trader',
    excerpt: 'A comprehensive guide on how freelancers can send invoices to themselves for expense tracking or respond to "invoice me" requests from clients professionally.',
    category: 'Best Practices',
    readTime: '9 min read',
    publishDate: '2024-02-12',
    image: 'https://images.pexels.com/photos/6863332/pexels-photo-6863332.jpeg?auto=compress&cs=tinysrgb&w=800'
  },
  {
    slug: 'collect-outstanding-unpaid-invoices',
    title: 'How to Collect Outstanding Invoices and Unpaid Invoices',
    excerpt: 'Learn proven strategies and best practices for collecting overdue payments from clients. Discover effective communication techniques, legal options, and automation tools to improve your cash flow.',
    category: 'Payment Collection',
    readTime: '8 min read',
    publishDate: '2024-02-10',
    image: 'https://images.pexels.com/photos/6289065/pexels-photo-6289065.jpeg?auto=compress&cs=tinysrgb&w=800'
  },
  {
    slug: 'invoice-message-email-best-practices',
    title: 'What to Include in an Invoice Message or Email',
    excerpt: 'Master the art of professional invoice communication. Learn what essential information to include in your invoice emails to ensure timely payments and maintain strong client relationships.',
    category: 'Best Practices',
    readTime: '6 min read',
    publishDate: '2024-02-08',
    image: 'https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800'
  },
  {
    slug: 'handle-past-due-invoice',
    title: 'How to Handle a Past Due Invoice',
    excerpt: 'Take control of late payments with our comprehensive guide. Discover step-by-step approaches to handle past due invoices professionally while preserving business relationships.',
    category: 'Payment Collection',
    readTime: '7 min read',
    publishDate: '2024-02-05',
    image: 'https://images.pexels.com/photos/6694543/pexels-photo-6694543.jpeg?auto=compress&cs=tinysrgb&w=800'
  }
]

const BlogPage = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')

  const categories = ['All', ...Array.from(new Set(articles.map(a => a.category)))]

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || article.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Invoice Management Resources</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Expert guides and best practices to help you get paid faster, manage invoices efficiently, and grow your business.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and Filter Bar */}
        <div className="mb-12 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Showing {filteredArticles.length} {filteredArticles.length === 1 ? 'article' : 'articles'}
          </div>
        </div>

        {/* Articles Grid */}
        {filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredArticles.map(article => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}`}
                className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                {/* Article Image */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>

                {/* Article Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                    <span className="text-sm text-gray-500">{article.readTime}</span>
                  </div>

                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {article.title}
                  </h2>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {new Date(article.publishDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                    <span className="text-blue-600 font-semibold group-hover:translate-x-2 transition-transform">
                      Read more →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Create Your Invoice?</h2>
          <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
            Stop reading and start earning. Generate professional invoices in seconds with our free tool.
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg"
          >
            Create Invoice Now
          </Link>
        </div>
      </div>
    </div>
  )
}

export default BlogPage